import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiService } from '@/services/api';
import { User } from '@/types';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        try {
          set({ isLoading: true, error: null });

          const response = await apiService.login(email, password);

          if (response.success && response.data) {
            const { user, tokens } = response.data;

            // Store tokens
            apiService.setAuthToken(tokens.accessToken);
            apiService.setRefreshToken(tokens.refreshToken);

            // Update state
            set({ user, isLoading: false });
          } else {
            throw new Error(response.message || 'Login failed');
          }
        } catch (error: any) {
          set({
            error: error.message || 'Login failed',
            isLoading: false
          });
          throw error;
        }
      },

      register: async (email: string, username: string, password: string) => {
        try {
          set({ isLoading: true, error: null });

          const response = await apiService.register({ email, username, password });

          if (response.success && response.data) {
            const { user, tokens } = response.data;

            // Store tokens
            apiService.setAuthToken(tokens.accessToken);
            apiService.setRefreshToken(tokens.refreshToken);

            // Update state
            set({ user, isLoading: false });
          } else {
            throw new Error(response.message || 'Registration failed');
          }
        } catch (error: any) {
          set({
            error: error.message || 'Registration failed',
            isLoading: false
          });
          throw error;
        }
      },

      logout: () => {
        try {
          // Call logout endpoint (fire and forget)
          apiService.logout().catch(() => {
            // Ignore errors during logout
          });
        } finally {
          // Clear tokens and state
          apiService.clearTokens();
          set({ user: null, error: null, isLoading: false });
        }
      },

      refreshProfile: async () => {
        try {
          const { user } = get();
          if (!user) return;

          const response = await apiService.getProfile();

          if (response.success && response.data) {
            set({ user: response.data.user });
          }
        } catch (error: any) {
          console.error('Failed to refresh profile:', error);
          // Don't update state on profile refresh failure
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'auth-store',
      partialize: (state) => ({ user: state.user }),
      onRehydrateStorage: () => (state) => {
        if (state?.user && !apiService.isAuthenticated()) {
          state.user = null;
        }
      },
    }
  )
);